import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Grid,
    Pagination,
    Snackbar,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useNutritionTemplates } from '../../../hooks/useNutritionTemplates';
import PlanoNutricaoCard from './PlanoNutricaoCard';

function Nutrition() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [showCreatedMessage, setShowCreatedMessage] = useState(
        Boolean(location.state?.nutritionPlanCreated)
    );

    const {
        data: plans,
        isLoading,
        error,
        count,
        paginationModel,
        setPaginationModel
    } = useNutritionTemplates();

    const handlePageChange = (_, newPage) => {
        setPaginationModel((previous) => ({
            ...previous,
            page: newPage - 1
        }));
    };

    const closeCreatedMessage = () => {
        setShowCreatedMessage(false);
        navigate(location.pathname, { replace: true, state: null });
    };

    return (
        <Box sx={{ minHeight: 'calc(100vh - 80px)', p: 2, bgcolor: '#F4F6F8' }}>
            <Snackbar
                open={showCreatedMessage}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={closeCreatedMessage}
            >
                <Alert severity="success" variant="filled">
                    {t('nutrition.createSuccess')}
                </Alert>
            </Snackbar>

            <Box
                component="header"
                sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
            >
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/nutricao/criar')}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 245,
                        height: 46,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        textTransform: 'none'
                    }}
                >
                    {t('nutrition.add_plan')}
                </Button>
            </Box>

            <Card
                variant="outlined"
                sx={{
                    p: 4,
                    borderColor: '#E0E5E9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
                }}
            >
                <Grid container spacing={3}>
                    {isLoading ? (
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    ) : error ? (
                        <Grid item xs={12}>
                            <Typography color="error" align="center">
                                {t('nutrition.load_error')}
                            </Typography>
                        </Grid>
                    ) : plans.length === 0 ? (
                        <Grid item xs={12}>
                            <Typography align="center">
                                {t('nutrition.none_available')}
                            </Typography>
                        </Grid>
                    ) : (
                        <>
                            {plans.map((plan) => (
                                <Grid key={plan.uuid} item lg={4} md={6} sm={6} xs={12}>
                                    <PlanoNutricaoCard
                                        planName={plan.name}
                                        planDesc={plan.description}
                                        pacientType={t('nutrition.title')}
                                        onClick={() => navigate(`/nutricao/${plan.uuid}`)}
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <Pagination
                                        count={Math.ceil(
                                            count / paginationModel.pageSize
                                        )}
                                        page={paginationModel.page + 1}
                                        onChange={handlePageChange}
                                    />
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Card>
        </Box>
    );
}

export default Nutrition;
